import styled, { css } from 'styled-components';

import { TextButton } from '@creditornot/cb-button';
import { elevationClassName } from '@creditornot/cb-ingredients';
import { color } from '@creditornot/cb-ingredients/design-tokens';

import { media } from '../../../media';

type ActionButtonPosition = 'right' | 'bottom';

export type SnackbarContentProps = React.HTMLAttributes<HTMLDivElement> & {
  message: string;
  actionButtonPosition?: ActionButtonPosition;
  actionButtonText?: string;
  onActionClick?: () => void;
  dataTestId?: string;
};

const Root = styled.div<{
  $actionButtonPosition: ActionButtonPosition;
}>`
  display: flex;
  gap: 16px;
  justify-content: space-between;
  border-radius: 8px;
  background-color: ${color.bgSurfaceInverse};
  color: ${color.textInverse};
  padding: 16px;
  margin-top: 8px;
  overflow: auto;

  ${({ $actionButtonPosition }) =>
    $actionButtonPosition === 'bottom' &&
    css`
      display: block;
    `}

  @media ${media.md} {
    padding: 24px;
  }
`;

const Message = styled.div`
  white-space: pre-wrap;
`;

const ActionButton = styled(TextButton)<{ actionButtonPosition: ActionButtonPosition }>`
  ${({ actionButtonPosition }) =>
    actionButtonPosition === 'bottom' &&
    css`
      float: right;
      padding-top: 12px;
    `}
`;

export const SnackbarContent = ({
  message,
  actionButtonPosition = 'right',
  onActionClick,
  actionButtonText,
  dataTestId,
}: SnackbarContentProps) => {
  return (
    <Root className={elevationClassName.medium} $actionButtonPosition={actionButtonPosition}>
      <Message data-test-id={dataTestId} role="status" aria-live="polite">
        {message}
      </Message>
      {actionButtonText && (
        <ActionButton onClick={onActionClick} actionButtonPosition={actionButtonPosition}>
          {actionButtonText}
        </ActionButton>
      )}
    </Root>
  );
};
